import type { Mark, Node as ProseMirrorNode } from "prosemirror-model";

import { formatMentionDate } from "./parse.js";
import {
  MIXABLE_MARKS,
  MarkName,
  NodeName,
  booleanAttr,
  numberAttr,
  stringAttr,
  type OrderedListStyle,
} from "../schema/index.js";
import { escapeEmbedUrl } from "./media.js";

export interface SerializeOptions {
  /**
   * Portable mode, used for markdown leaving the system (export, clipboard).
   * Empty paragraphs become blank lines, hard breaks become two trailing
   * spaces, and external mentions emit their real URL.
   */
  commonmark?: boolean;
}

/* ---------------------------------------------------------------- escaping */

const ALWAYS_ESCAPED = new Set(["`", "*", "\\", "~"]);
const LINE_START_ESCAPED = new Set([":", "#", "-", "*", "+"]);

/**
 * Escapes one text run. The `[` rule looks at the remainder of this run only —
 * a bracket and a later `](` in the same run means the bracket is escaped, but
 * a link's own text is a separate run and so never triggers it.
 */
export function escapeRun(text: string, startOfLine: boolean, inTable: boolean): string {
  let result = "";
  let lineStart = startOfLine;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text.charAt(i);
    if (ch === "\n") {
      result += ch;
      lineStart = true;
      continue;
    }
    if (lineStart) {
      const numbered = /^(\d+)\./.exec(text.slice(i));
      if (numbered?.[1]) {
        result += `${numbered[1]}\\.`;
        i += numbered[0].length - 1;
        lineStart = false;
        continue;
      }
      if (LINE_START_ESCAPED.has(ch)) {
        result += `\\${ch}`;
        lineStart = false;
        continue;
      }
    }
    lineStart = false;
    if (ALWAYS_ESCAPED.has(ch)) {
      result += `\\${ch}`;
      continue;
    }
    if (ch === "|" && inTable) {
      result += "\\|";
      continue;
    }
    if (ch === "[") {
      const lineEnd = text.indexOf("\n", i);
      const rest = lineEnd === -1 ? text.slice(i) : text.slice(i, lineEnd);
      result += rest.includes("](") ? "\\[" : "[";
      continue;
    }
    result += ch;
  }
  return result;
}

/** Link titles fall back through the three CommonMark quoting styles. */
function quoteTitle(title: string): string {
  if (!title.includes('"')) return ` "${title}"`;
  if (!title.includes("'")) return ` '${title}'`;
  return ` (${title})`;
}

function sanitizeLanguage(language: string | null): string {
  if (!language) return "";
  return language.replace(/`/g, "").trim().split(/\s+/)[0] ?? "";
}

function longestFenceRun(content: string): number {
  let longest = 0;
  const runs = /`{3,}/g;
  let match: RegExpExecArray | null;
  while ((match = runs.exec(content)) !== null) longest = Math.max(longest, match[0].length);
  return longest;
}

/** Bijective base-26: 1 → a, 26 → z, 27 → aa. */
export function alphaLabel(position: number, upper: boolean): string {
  let value = Math.max(1, position);
  let label = "";
  while (value > 0) {
    const remainder = (value - 1) % 26;
    label = String.fromCharCode(97 + remainder) + label;
    value = Math.floor((value - 1) / 26);
  }
  return upper ? label.toUpperCase() : label;
}

/** Height of a node's subtree; drives the toggle fence length. */
function subtreeHeight(node: ProseMirrorNode): number {
  if (node.childCount === 0) return 0;
  let deepest = 0;
  node.forEach((child) => {
    deepest = Math.max(deepest, subtreeHeight(child));
  });
  return deepest + 1;
}

/* ----------------------------------------------------------------- writing */

interface Atom {
  node: ProseMirrorNode;
  marks: readonly Mark[];
}

/** Marks that may be reordered and may shed whitespace at a run's edges. */
const MIXABLE = new Set<string>(MIXABLE_MARKS);

/**
 * Markdown lets mixable marks open and close in either order, so a mark that
 * is already open is moved to the position it occupies in the open set. That
 * is what keeps `~~struck **bold**~~` as one strikethrough run even though the
 * schema ranks `strong` above `strikethrough`.
 */
function reorderMixable(marks: readonly Mark[], open: readonly { mark: Mark }[]): readonly Mark[] {
  let result: Mark[] | null = null;
  for (let i = 0; i < marks.length; i += 1) {
    const current = (result ?? marks)[i];
    if (!current || !MIXABLE.has(current.type.name)) break;
    for (let j = 0; j < open.length; j += 1) {
      const other = open[j]?.mark;
      if (!other || !MIXABLE.has(other.type.name)) break;
      if (!current.eq(other)) continue;
      if (i !== j) {
        result ??= marks.slice();
        result.splice(i, 1);
        result.splice(j, 0, current);
      }
      break;
    }
  }
  return result ?? marks;
}

export interface BlockRange {
  index: number;
  from: number;
  to: number;
}

class Writer {
  delim = "";
  // The output is accumulated as chunks with the tail tracked separately:
  // inspecting the joined string on every write would make serialization
  // quadratic in document size.
  private chunks: string[] = [];
  private length = 0;
  private lastChar = "";
  private closedType: string | null = null;
  private inTightList = false;
  readonly blockRanges: BlockRange[] = [];

  constructor(
    readonly commonmark: boolean,
    readonly inTable: boolean,
  ) {}

  get out(): string {
    if (this.chunks.length > 1) this.chunks = [this.chunks.join("")];
    return this.chunks[0] ?? "";
  }

  private append(value: string): void {
    if (value === "") return;
    this.chunks.push(value);
    this.length += value.length;
    this.lastChar = value.charAt(value.length - 1);
  }

  atBlank(): boolean {
    return this.length === 0 || this.lastChar === "\n";
  }

  flushClose(size = 2): void {
    if (this.closedType === null) return;
    if (!this.atBlank()) this.append("\n");
    if (size > 1) {
      const trimmed = this.delim.replace(/\s+$/, "");
      for (let i = 1; i < size; i += 1) this.append(`${trimmed}\n`);
    }
    this.closedType = null;
  }

  write(content = ""): void {
    this.flushClose();
    if (this.delim !== "" && this.atBlank()) this.append(this.delim);
    this.append(content);
  }

  ensureNewLine(): void {
    if (!this.atBlank()) this.append("\n");
  }

  closeBlock(node: ProseMirrorNode): void {
    this.closedType = node.type.name;
  }

  text(value: string): void {
    const lines = value.split("\n");
    lines.forEach((line, index) => {
      this.write();
      this.append(line);
      if (index !== lines.length - 1) this.append("\n");
    });
  }

  wrapBlock(
    delim: string,
    firstDelim: string | null,
    node: ProseMirrorNode,
    body: () => void,
  ): void {
    const previous = this.delim;
    this.write(firstDelim ?? delim);
    this.delim += delim;
    body();
    this.delim = previous;
    this.closeBlock(node);
  }

  renderList(node: ProseMirrorNode, delim: string, firstDelim: (index: number) => string): void {
    if (this.closedType === node.type.name) this.flushClose(3);
    else if (this.inTightList) this.flushClose(1);
    const previous = this.inTightList;
    this.inTightList = true;
    node.forEach((child, _offset, index) => {
      if (index > 0) this.flushClose(1);
      this.wrapBlock(delim, firstDelim(index), node, () => {
        this.renderContent(child);
      });
    });
    this.inTightList = previous;
  }

  renderContent(node: ProseMirrorNode): void {
    node.forEach((child) => {
      this.renderBlock(child);
    });
  }

  renderDoc(node: ProseMirrorNode): void {
    node.forEach((child, _offset, index) => {
      const from = this.length;
      this.renderBlock(child);
      this.blockRanges.push({ index, from, to: this.length });
    });
  }

  /* --------------------------------------------------------------- blocks */

  renderBlock(node: ProseMirrorNode): void {
    switch (node.type.name) {
      case NodeName.paragraph:
        this.renderParagraph(node);
        break;
      case NodeName.heading: {
        const level = Math.min(6, Math.max(1, numberAttr(node, "level") ?? 1));
        this.write(`${"#".repeat(level)} `);
        this.text(this.renderInline(node, false));
        this.closeBlock(node);
        break;
      }
      case NodeName.blockquote:
        this.wrapBlock("> ", null, node, () => {
          this.renderContent(node);
        });
        break;
      case NodeName.bulletList:
        this.renderList(node, "  ", () => "* ");
        break;
      case NodeName.orderedList:
        this.renderOrderedList(node);
        break;
      case NodeName.checkboxList:
        this.renderList(node, "  ", (index) => {
          const child = node.child(index);
          return booleanAttr(child, "checked") ? "- [x] " : "- [ ] ";
        });
        break;
      case NodeName.codeBlock:
        this.renderCodeBlock(node);
        break;
      case NodeName.mathBlock:
        this.write("$$");
        this.ensureNewLine();
        this.text(node.textContent);
        this.ensureNewLine();
        this.write("$$");
        this.closeBlock(node);
        break;
      case NodeName.hr:
        this.write(`\n${stringAttr(node, "markup") ?? "---"}`);
        this.closeBlock(node);
        break;
      case NodeName.containerNotice:
        this.write(`\n:::${stringAttr(node, "style") ?? "info"}`);
        this.ensureNewLine();
        this.renderContent(node);
        this.write(":::");
        this.closeBlock(node);
        break;
      case NodeName.containerToggle: {
        const fence = "+".repeat(3 + Math.max(2, subtreeHeight(node)));
        this.write(fence);
        this.ensureNewLine();
        this.renderContent(node);
        this.write(fence);
        this.ensureNewLine();
        break;
      }
      case NodeName.table:
        this.renderTable(node);
        break;
      case NodeName.attachment: {
        const size = stringAttr(node, "size") ?? String(numberAttr(node, "size") ?? 0);
        const title = stringAttr(node, "title") ?? "";
        this.ensureNewLine();
        this.write(`[${title} ${size}](${stringAttr(node, "href") ?? ""})\n\n`);
        break;
      }
      case NodeName.video: {
        const title = stringAttr(node, "title") ?? "";
        const width = numberAttr(node, "width") ?? 0;
        const height = numberAttr(node, "height") ?? 0;
        this.ensureNewLine();
        this.write(
          `[${title} ${String(width)}x${String(height)}](${stringAttr(node, "src") ?? ""})\n\n`,
        );
        break;
      }
      case NodeName.embed: {
        const href = escapeEmbedUrl(stringAttr(node, "href") ?? "");
        this.ensureNewLine();
        this.write(`[${href}](${href})\n\n`);
        break;
      }
      default:
        // Any other block (an inline node promoted to block position) renders
        // through the inline path so nothing is silently dropped.
        this.text(this.renderNode({ node, marks: [] }, false, false));
        this.closeBlock(node);
        break;
    }
  }

  private renderParagraph(node: ProseMirrorNode): void {
    if (node.content.size === 0) {
      // An empty paragraph writes a lone backslash and does NOT close its
      // block, so no blank line follows it.
      this.write(this.commonmark ? "\n" : "\\\n");
      return;
    }
    this.text(this.renderInline(node, true));
    this.closeBlock(node);
  }

  private renderOrderedList(node: ProseMirrorNode): void {
    const style = (stringAttr(node, "listStyle") ?? "number") as OrderedListStyle;
    const order = numberAttr(node, "order") ?? 1;
    const labels: string[] = [];
    for (let i = 0; i < node.childCount; i += 1) {
      labels.push(
        style === "number" ? String(order + i) : alphaLabel(order + i, style === "upper-alpha"),
      );
    }
    const width = labels.reduce((max, label) => Math.max(max, label.length), 1);
    this.write("\n");
    const delim = style === "number" ? " ".repeat(width + 2) : "    ";
    this.renderList(node, delim, (index) => {
      const label = labels[index] ?? "";
      const pad = style === "number" ? " ".repeat(width - label.length) : "";
      return `${pad}${label}. `;
    });
  }

  private renderCodeBlock(node: ProseMirrorNode): void {
    const content = node.textContent;
    const fence = "`".repeat(Math.max(3, longestFenceRun(content) + 1));
    this.write(fence + sanitizeLanguage(stringAttr(node, "language")));
    this.ensureNewLine();
    this.text(content);
    this.ensureNewLine();
    this.write(fence);
    this.closeBlock(node);
  }

  /* --------------------------------------------------------------- tables */

  private renderTable(node: ProseMirrorNode): void {
    const rows: ProseMirrorNode[] = [];
    node.forEach((row) => rows.push(row));
    const header = rows[0];
    if (!header) return;

    const widths: number[] = [];
    header.forEach((cell) => {
      widths.push(Math.max(3, cell.textContent.length));
    });
    const alignments: (string | null)[] = [];
    header.forEach((cell) => {
      alignments.push(stringAttr(cell, "alignment"));
    });

    const renderRow = (row: ProseMirrorNode): string => {
      const cells: string[] = [];
      row.forEach((cell, _offset, index) => {
        const width = widths[index] ?? 3;
        cells.push(this.renderCell(cell).padEnd(width));
      });
      return `| ${cells.join(" | ")} |`;
    };

    const separator = widths
      .map((width, index) => {
        switch (alignments[index]) {
          case "left":
            return `|:${"-".repeat(width + 1)}`;
          case "right":
            return `|${"-".repeat(width + 1)}:`;
          case "center":
            return `|:${"-".repeat(width)}:`;
          default:
            return `|${"-".repeat(width + 2)}`;
        }
      })
      .join("");

    const lines = [renderRow(header), `${separator}|`];
    for (const row of rows.slice(1)) lines.push(renderRow(row));

    this.flushClose(1);
    this.text(`\n${lines.join("\n")}`);
    this.ensureNewLine();
    this.closeBlock(node);
  }

  private renderCell(cell: ProseMirrorNode): string {
    const inner = new Writer(this.commonmark, true);
    inner.renderContent(cell);
    return inner.out.replace(/^\n+/, "").replace(/\n+$/, "").replace(/\n/g, "<br>");
  }

  /* --------------------------------------------------------------- inline */

  renderInline(parent: ProseMirrorNode, startOfLine: boolean): string {
    const atoms = this.expelWhitespace(this.flatten(parent));
    const autolinks = this.findAutolinks(atoms);
    const open: { mark: Mark; close: string }[] = [];
    let result = "";
    let atLineStart = startOfLine;

    const closeTo = (depth: number): void => {
      while (open.length > depth) {
        const entry = open.pop();
        if (entry) result += entry.close;
      }
    };

    atoms.forEach((atom, index) => {
      const marks = reorderMixable(atom.marks, open);
      let shared = 0;
      while (shared < open.length && shared < marks.length) {
        const active = open[shared];
        const next = marks[shared];
        if (!active || !next || !active.mark.eq(next)) break;
        shared += 1;
      }
      closeTo(shared);
      for (let i = shared; i < marks.length; i += 1) {
        const mark = marks[i];
        if (!mark) continue;
        const delimiters = this.markDelimiters(mark, atoms, index, autolinks.has(index));
        result += delimiters.open;
        open.push({ mark, close: delimiters.close });
      }
      const raw = autolinks.has(index);
      result += this.renderNode(atom, atLineStart, raw);
      if (atom.node.type.name === NodeName.br) {
        atLineStart = this.commonmark && !this.inTable;
      } else {
        atLineStart = false;
      }
    });
    closeTo(0);
    return result;
  }

  private flatten(parent: ProseMirrorNode): Atom[] {
    const atoms: Atom[] = [];
    parent.forEach((child) => {
      atoms.push({ node: child, marks: child.marks });
    });
    return atoms;
  }

  /** Moves whitespace at the edges of a mark run outside its delimiters. */
  private expelWhitespace(atoms: Atom[]): Atom[] {
    const result: Atom[] = [];
    atoms.forEach((atom, index) => {
      const text = atom.node.text;
      const expellable = atom.marks.filter((mark) => MIXABLE.has(mark.type.name));
      const isCode = atom.marks.some((mark) => mark.type.name === MarkName.codeInline);
      if (!atom.node.isText || text === undefined || expellable.length === 0 || isCode) {
        result.push(atom);
        return;
      }
      // Only the first and last atom of a run may shed whitespace; doing it
      // mid-run would split the run into two sets of delimiters.
      const shares = (other: Atom | undefined): boolean =>
        other !== undefined && expellable.some((mark) => other.marks.some((m) => m.eq(mark)));
      const leading = shares(atoms[index - 1]) ? "" : (/^\s+/.exec(text)?.[0] ?? "");
      const trailing =
        shares(atoms[index + 1]) || text.length === leading.length
          ? ""
          : (/\s+$/.exec(text)?.[0] ?? "");
      if (leading === "" && trailing === "") {
        result.push(atom);
        return;
      }
      const core = text.slice(leading.length, text.length - trailing.length);
      const outer = atom.marks.filter((mark) => !MIXABLE.has(mark.type.name));
      if (leading !== "") {
        result.push({ node: atom.node.type.schema.text(leading, outer), marks: outer });
      }
      if (core !== "") {
        result.push({
          node: atom.node.type.schema.text(core, atom.marks),
          marks: atom.marks,
        });
      }
      if (trailing !== "") {
        result.push({ node: atom.node.type.schema.text(trailing, outer), marks: outer });
      }
    });
    return result;
  }

  /**
   * A link whose only content is a text node equal to its href, with the link
   * as the outermost mark and no title, collapses to `<href>`.
   */
  private findAutolinks(atoms: Atom[]): Set<number> {
    const found = new Set<number>();
    atoms.forEach((atom, index) => {
      const link = atom.marks[0];
      if (link?.type.name !== MarkName.link) return;
      if (atom.marks.length !== 1 || !atom.node.isText) return;
      const href = typeof link.attrs.href === "string" ? link.attrs.href : "";
      if (typeof link.attrs.title === "string" && link.attrs.title !== "") return;
      if (!/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(href)) return;
      if (atom.node.text !== href) return;
      const before = atoms[index - 1];
      const after = atoms[index + 1];
      if (before?.marks.some((mark) => mark.eq(link))) return;
      if (after?.marks.some((mark) => mark.eq(link))) return;
      found.add(index);
    });
    return found;
  }

  private markDelimiters(
    mark: Mark,
    atoms: Atom[],
    index: number,
    autolink: boolean,
  ): { open: string; close: string } {
    switch (mark.type.name) {
      case MarkName.strong:
        return { open: "**", close: "**" };
      case MarkName.em:
        return { open: "*", close: "*" };
      case MarkName.underline:
        return { open: "__", close: "__" };
      case MarkName.strikethrough:
        return { open: "~~", close: "~~" };
      case MarkName.highlight:
        return { open: "==", close: "==" };
      case MarkName.placeholder:
        return { open: "!!", close: "!!" };
      case MarkName.comment:
        return { open: "", close: "" };
      case MarkName.codeInline: {
        let content = "";
        for (let i = index; i < atoms.length; i += 1) {
          const atom = atoms[i];
          if (!atom?.marks.some((other) => other.eq(mark))) break;
          content += atom.node.text ?? "";
        }
        const runs = /`+/g;
        let longest = 0;
        let match: RegExpExecArray | null;
        while ((match = runs.exec(content)) !== null) longest = Math.max(longest, match[0].length);
        const ticks = "`".repeat(longest + 1);
        const pad = longest > 0 ? " " : "";
        return { open: ticks + pad, close: pad + ticks };
      }
      case MarkName.link: {
        if (autolink) return { open: "<", close: ">" };
        const href = typeof mark.attrs.href === "string" ? mark.attrs.href : "";
        const title = typeof mark.attrs.title === "string" ? mark.attrs.title : "";
        return { open: "[", close: `](${href}${title === "" ? "" : quoteTitle(title)})` };
      }
      default:
        return { open: "", close: "" };
    }
  }

  private renderNode(atom: Atom, atLineStart: boolean, raw: boolean): string {
    const node = atom.node;
    if (node.isText) {
      const text = node.text ?? "";
      if (raw) return text;
      const isCode = atom.marks.some((mark) => mark.type.name === MarkName.codeInline);
      if (isCode) return this.inTable ? text.replace(/\|/g, "\\|") : text;
      return escapeRun(text, atLineStart, this.inTable);
    }
    switch (node.type.name) {
      case NodeName.br:
        if (this.inTable) return "<br>";
        return this.commonmark ? "  \n" : "\\n";
      case NodeName.image:
        return this.renderImage(node);
      case NodeName.mention:
        return this.renderMention(node);
      case NodeName.emoji: {
        const name = stringAttr(node, "data-name") ?? "";
        return name === "" ? "" : `:${name}:`;
      }
      case NodeName.mathInline:
        return `$${node.textContent}$`;
      default:
        return node.textContent;
    }
  }

  private renderImage(node: ProseMirrorNode): string {
    const alt = (stringAttr(node, "alt") ?? "").replace(/\n/g, "");
    const src = stringAttr(node, "src") ?? "";
    const source = stringAttr(node, "source");
    const layoutClass = stringAttr(node, "layoutClass");
    const title = stringAttr(node, "title");
    const width = numberAttr(node, "width");
    const height = numberAttr(node, "height");

    const parts: string[] = [];
    if (source !== null) parts.push(`source=${source}`);
    const label = layoutClass ?? title;
    if (label !== null && label !== "") parts.push(label);
    let slot = parts.join(" ");
    if (width !== null || height !== null) {
      slot += ` =${width === null ? "" : String(width)}x${height === null ? "" : String(height)}`;
    }
    const titleSlot = slot === "" ? "" : ` "${slot}"`;
    // A leading space keeps an image at the start of an indented context from
    // being read as code; inside a list item the indent already does that.
    const lead = this.delim === "" && !this.inTable ? " " : "";
    return `${lead}![${alt}](${src}${titleSlot})`;
  }

  private renderMention(node: ProseMirrorNode): string {
    const type = stringAttr(node, "type") ?? "user";
    const modelId = stringAttr(node, "modelId") ?? "";
    // A date mention shows the date its `modelId` holds, not the label the
    // markdown carried.
    const label =
      (type === "date" ? formatMentionDate(modelId) : null) ?? stringAttr(node, "label") ?? "";
    const href = stringAttr(node, "href");
    const anchorId = stringAttr(node, "anchorId");
    if (type === "document") {
      const anchor = anchorId === null || anchorId === "" ? "" : `#${anchorId}`;
      return `[${label}](/doc/${modelId}${anchor})`;
    }
    if (type === "collection") return `[${label}](/collection/${modelId})`;
    if (this.commonmark && href !== null && href !== "") return `@[${label}](${href})`;
    return `@[${label}](mention://${stringAttr(node, "id") ?? ""}/${type}/${modelId})`;
  }
}

/* --------------------------------------------------------------------- API */

export function serializeMarkdown(doc: ProseMirrorNode, options: SerializeOptions = {}): string {
  const writer = new Writer(options.commonmark === true, false);
  writer.renderDoc(doc);
  return writer.out;
}

/** Serializes and reports the markdown range each top-level block occupies. */
export function serializeWithBlockRanges(
  doc: ProseMirrorNode,
  options: SerializeOptions = {},
): { markdown: string; blocks: BlockRange[] } {
  const writer = new Writer(options.commonmark === true, false);
  writer.renderDoc(doc);
  return { markdown: writer.out, blocks: writer.blockRanges };
}

/**
 * What the API's `text` field returns: the internal serialization with lone
 * backslash lines rewritten to blank lines, then trimmed. The rewrite is a
 * single non-overlapping pass, so two adjacent empty paragraphs leak the
 * second backslash — reproduced deliberately.
 */
export function toApiText(doc: ProseMirrorNode): string {
  return serializeMarkdown(doc)
    .replace(/(?:^|\n)\\(?:\n|$)/g, "\n\n")
    .trim();
}
